import React, { useEffect, useState, useCallback } from 'react';
import {
  adminReportService,
  ReportColumn,
  UsageReportResponse,
  ScheduledReport,
  UsageRequestBody,
} from '../services/adminReportService';

const COLUMN_LABELS: Record<ReportColumn, { title: string; group: string; description: string }> = {
  activity_status: { title: 'License Utilization', group: 'Overview', description: 'Active vs. total licenses for the company' },
  customers_created: { title: 'Customers Created', group: 'Activity', description: 'Customers each user created in the period' },
  customers_worked_in: { title: 'Customers Worked In', group: 'Activity', description: 'Distinct customers each user touched' },
  reports_total: { title: 'Reports Generated', group: 'Activity', description: 'Reports created per user' },
  reports_by_workflow: { title: 'Reports — Workflow Breakdown', group: 'Activity', description: 'Reports broken down by workflow type (PDF only)' },
  documents_uploaded: { title: 'Documents Uploaded', group: 'Activity', description: 'Document upload count per user' },
  login_count: { title: 'Logins', group: 'Engagement', description: 'Successful logins in the period' },
  last_activity: { title: 'Last Activity', group: 'Engagement', description: 'Last recorded login/event timestamp' },
};

const ALL_COLUMNS: ReportColumn[] = Object.keys(COLUMN_LABELS) as ReportColumn[];

const DEFAULT_COLUMNS: ReportColumn[] = [
  'customers_created',
  'customers_worked_in',
  'reports_total',
  'documents_uploaded',
  'login_count',
  'last_activity',
];

const UsageReportsSection: React.FC<{ adminEmail: string }> = ({ adminEmail }) => {
  const [columns, setColumns] = useState<Set<ReportColumn>>(new Set(DEFAULT_COLUMNS));
  const [dateMode, setDateMode] = useState<'last_month' | 'last_30d' | 'custom'>('last_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [preview, setPreview] = useState<UsageReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [schedules, setSchedules] = useState<ScheduledReport[]>([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleName, setScheduleName] = useState('Monthly usage report');
  const [scheduleRecipients, setScheduleRecipients] = useState<string[]>([adminEmail]);
  const [scheduleRecipientInput, setScheduleRecipientInput] = useState('');
  const [scheduleDateMode, setScheduleDateMode] = useState<'last_month' | 'last_30d'>('last_month');
  const [scheduleColumns, setScheduleColumns] = useState<Set<ReportColumn>>(new Set(DEFAULT_COLUMNS));

  const loadSchedules = useCallback(async () => {
    try {
      const items = await adminReportService.listScheduled();
      setSchedules(items);
    } catch (e: any) {
      console.error('Failed to load scheduled reports', e);
    }
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const buildRequestBody = (cols: Set<ReportColumn>): UsageRequestBody => ({
    columns: Array.from(cols),
    dateRange: dateMode === 'custom'
      ? { from: customFrom, to: customTo }
      : { mode: dateMode },
  });

  const togglePreviewColumn = (c: ReportColumn) => {
    const next = new Set(columns);
    if (next.has(c)) next.delete(c); else next.add(c);
    setColumns(next);
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminReportService.preview(buildRequestBody(columns));
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    setError('');
    try {
      const fileName = `usage-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      await adminReportService.downloadPdf(buildRequestBody(columns), fileName);
    } catch (e: any) {
      setError(e.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = () => {
    const v = scheduleRecipientInput.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
    if (scheduleRecipients.includes(v)) return;
    setScheduleRecipients([...scheduleRecipients, v]);
    setScheduleRecipientInput('');
  };

  const handleRemoveRecipient = (email: string) => {
    setScheduleRecipients(scheduleRecipients.filter((e) => e !== email));
  };

  const toggleScheduleColumn = (c: ReportColumn) => {
    const next = new Set(scheduleColumns);
    if (next.has(c)) next.delete(c); else next.add(c);
    setScheduleColumns(next);
  };

  const handleCreateSchedule = async () => {
    setError('');
    if (scheduleRecipients.length === 0) { setError('At least one recipient is required'); return; }
    if (scheduleColumns.size === 0) { setError('At least one column is required'); return; }
    try {
      await adminReportService.createScheduled({
        name: scheduleName.trim() || 'Monthly usage report',
        columns: Array.from(scheduleColumns),
        dateRangeMode: scheduleDateMode,
        recipients: scheduleRecipients,
      });
      setShowScheduleForm(false);
      setScheduleName('Monthly usage report');
      setScheduleRecipients([adminEmail]);
      setScheduleColumns(new Set(DEFAULT_COLUMNS));
      await loadSchedules();
    } catch (e: any) {
      setError(e.message || 'Failed to create schedule');
    }
  };

  const handleToggleEnabled = async (s: ScheduledReport) => {
    try {
      await adminReportService.updateScheduled(s.id, { enabled: !s.enabled });
      await loadSchedules();
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    try {
      await adminReportService.deleteScheduled(id);
      await loadSchedules();
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  };

  const handleRunNow = async (id: string) => {
    try {
      const result = await adminReportService.runScheduledNow(id);
      alert(`Report sent to ${result.sent} of ${result.total} recipient(s).`);
      await loadSchedules();
    } catch (e: any) {
      setError(e.message || 'Failed to run');
    }
  };

  const groups = Array.from(new Set(ALL_COLUMNS.map((c) => COLUMN_LABELS[c].group)));

  return (
    <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-md border border-gray-200 dark:border-dark-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-secondary dark:text-dark-text">Usage Reports</h2>
        <button
          onClick={() => setShowScheduleForm(!showScheduleForm)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
        >
          {showScheduleForm ? 'Hide schedule form' : 'Schedule monthly email'}
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded text-sm">{error}</div>
      )}

      {/* Manual report builder */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {groups.map((group) => (
            <div key={group}>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{group}</h4>
              <div className="space-y-1">
                {ALL_COLUMNS.filter((c) => COLUMN_LABELS[c].group === group).map((c) => (
                  <label key={c} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={columns.has(c)}
                      onChange={() => togglePreviewColumn(c)}
                    />
                    <span>
                      <span className="font-medium">{COLUMN_LABELS[c].title}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">{COLUMN_LABELS[c].description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Period:</span>
          <select
            value={dateMode}
            onChange={(e) => setDateMode(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="last_month">Last month</option>
            <option value="last_30d">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
          {dateMode === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              <span className="text-gray-500">to</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            disabled={loading || columns.size === 0}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm disabled:opacity-50"
          >
            {loading ? 'Generating…' : 'Preview'}
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || columns.size === 0}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm disabled:opacity-50"
          >
            Download PDF
          </button>
        </div>

        {preview && (
          <div className="mt-6 overflow-x-auto">
            {preview.licenseUtilization && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                License utilization: <strong>{preview.licenseUtilization.used}</strong> / {preview.licenseUtilization.total}
              </p>
            )}
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-4 font-medium">User</th>
                  {preview.columns.includes('customers_created') && <th className="py-2 pr-4 font-medium text-right">Customers Created</th>}
                  {preview.columns.includes('customers_worked_in') && <th className="py-2 pr-4 font-medium text-right">Customers Worked</th>}
                  {preview.columns.includes('reports_total') && <th className="py-2 pr-4 font-medium text-right">Reports</th>}
                  {preview.columns.includes('documents_uploaded') && <th className="py-2 pr-4 font-medium text-right">Documents</th>}
                  {preview.columns.includes('login_count') && <th className="py-2 pr-4 font-medium text-right">Logins</th>}
                  {preview.columns.includes('last_activity') && <th className="py-2 font-medium">Last Activity</th>}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.user.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <td className="py-2 pr-4">
                      <div className="font-medium text-gray-900 dark:text-white">{row.user.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.user.email}</div>
                    </td>
                    {preview.columns.includes('customers_created') && <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{row.customersCreated ?? 0}</td>}
                    {preview.columns.includes('customers_worked_in') && <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{row.customersWorkedIn ?? 0}</td>}
                    {preview.columns.includes('reports_total') && <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{row.reportsTotal ?? 0}</td>}
                    {preview.columns.includes('documents_uploaded') && <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{row.documentsUploaded ?? 0}</td>}
                    {preview.columns.includes('login_count') && <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{row.loginCount ?? 0}</td>}
                    {preview.columns.includes('last_activity') && (
                      <td className="py-2 text-xs text-gray-600 dark:text-gray-400">
                        {row.lastActivity ? new Date(row.lastActivity).toLocaleString() : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule form */}
      {showScheduleForm && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-3">Schedule monthly email</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule name</label>
              <input
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period for each report</label>
              <select
                value={scheduleDateMode}
                onChange={(e) => setScheduleDateMode(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="last_month">Last calendar month</option>
                <option value="last_30d">Last 30 days</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Columns</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {ALL_COLUMNS.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={scheduleColumns.has(c)} onChange={() => toggleScheduleColumn(c)} />
                    {COLUMN_LABELS[c].title}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recipients</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {scheduleRecipients.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                    {e}
                    <button onClick={() => handleRemoveRecipient(e)} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="add another email"
                  value={scheduleRecipientInput}
                  onChange={(e) => setScheduleRecipientInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRecipient())}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <button onClick={handleAddRecipient} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm">
                  Add
                </button>
              </div>
            </div>

            <button
              onClick={handleCreateSchedule}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm"
            >
              Create schedule
            </button>
          </div>
        </div>
      )}

      {/* Existing schedules */}
      {schedules.length > 0 && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-3">Active schedules</h3>
          <ul className="space-y-2">
            {schedules.map((s) => (
              <li key={s.id} className="border border-gray-200 dark:border-gray-700 rounded p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white text-sm">{s.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {s.dateRangeMode === 'last_month' ? 'Last calendar month' : 'Last 30 days'} •
                    Recipients: {s.recipients.join(', ')} •
                    Next run: {new Date(s.nextRunAt).toLocaleDateString()}
                    {s.lastRunAt && ` • Last run: ${new Date(s.lastRunAt).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleRunNow(s.id)} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200">Run now</button>
                  <button onClick={() => handleToggleEnabled(s)} className={`px-2 py-1 text-xs rounded ${s.enabled ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                    {s.enabled ? 'Pause' : 'Enable'}
                  </button>
                  <button onClick={() => handleDeleteSchedule(s.id)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UsageReportsSection;
